import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
  useSecondaryColor,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  multihubLogoMonoWhiteUrl,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const n = val.trim().toLowerCase();
  return n !== '' && n !== 'false' && n !== 'null' && n !== 'undefined';
}

// Default company logos keyed by how the footer's secondary surface reads.
// When the secondary background is light (so text-on-secondary is dark) we need
// a dark logo; otherwise the white/light logo reads correctly on the dark band.
const COMPANY_LOGO_FOR_LIGHT_SECONDARY =
  'https://multihubmedia.com/company/5695708787113984/019d02ed-b73c-7581-ab8a-b01c3434f867.webp';
const COMPANY_LOGO_FOR_DARK_SECONDARY =
  'https://multihubmedia.com/company/5695708787113984/019d02ed-bfec-74fd-b5d0-5a699636306b.webp';

// Relative luminance (0 = black, 1 = white) for a CSS hex / rgb() color.
// Returns null when the color can't be parsed.
function relativeLuminance(color: string | null | undefined): number | null {
  if (!color) return null;
  const c = color.trim().toLowerCase();
  let r: number, g: number, b: number;

  const hexMatch = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split('').map((ch) => ch + ch).join('');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    const rgbMatch = c.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!rgbMatch) return null;
    r = parseInt(rgbMatch[1], 10);
    g = parseInt(rgbMatch[2], 10);
    b = parseInt(rgbMatch[3], 10);
  }

  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function isLightColor(color: string | null | undefined): boolean {
  const lum = relativeLuminance(color);
  return lum !== null && lum > 0.5;
}

function isDarkColor(color: string | null | undefined): boolean {
  const lum = relativeLuminance(color);
  return lum !== null && lum <= 0.5;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toUpperCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return minutes === '00' ? `${hours} ${period}` : `${hours}:${minutes} ${period}`;
}

function formatHoursValue(
  openRaw: string | null | undefined,
  closeRaw: string | null | undefined,
): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment';
  if (!hasValue(open) && !hasValue(close)) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!hasValue(open) || !hasValue(close)) return 'Closed';
  return `${formatTime(open)} – ${formatTime(close)}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface GroupedHour { label: string; hours: string }

function normalizeDay(d: string): string { return d.trim().toLowerCase().slice(0, 3); }

function groupOfficeHours(raw: OfficeHour[]): GroupedHour[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  const groups: GroupedHour[] = [];
  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const hoursStr = formatHoursValue(entry.open_time, entry.close_time);
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' – ');
      const start = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${start} – ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }
  return groups;
}

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

function FacebookSvg() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramSvg() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function LinkedInSvg() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

// ─── Slot group definitions ───────────────────────────────────────────────────

function navLinkDef(label: string, defaultLabel: string, defaultUrl: string): SlotGroupDefinition {
  return {
    label,
    fields: {
      url: { label: 'URL', type: 'url', defaultValue: defaultUrl },
      label: { label: 'Link Text', type: 'text', defaultValue: defaultLabel },
      _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    },
  };
}

// Property address — editable text + link. The address text defaults to the
// property's full address; the URL defaults to empty and falls back in-component
// to usePropertyAddressUrl() (a mobile-safe Google Maps link).
const propertyAddressDef: SlotGroupDefinition = {
  label: 'Property Address',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Address', type: 'text', defaultField: 'property_full_address' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Property phone — editable phone, rendered as a tel: link.
const propertyPhoneDef: SlotGroupDefinition = {
  label: 'Property Phone',
  fields: {
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '(555) 555-5555',
    },
  },
};

const companyLogoDef: SlotGroupDefinition = {
  label: 'Company Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      // Default resolved in-component from the secondary-color contrast (see Footer).
      defaultValue: '',
      suggestedFields: ['property_company_mono_white_logo', 'property_company_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'American Landmark Apartments' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url', defaultValue: '/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const companyInstagramDef: SlotGroupDefinition = {
  label: 'Company Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.instagram.com/americanlandmarkapartments/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const companyLinkedinDef: SlotGroupDefinition = {
  label: 'Company LinkedIn',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const companyFacebookDef: SlotGroupDefinition = {
  label: 'Company Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.facebook.com/AmericanLandmarkApts/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo', type: 'image', defaultField: 'property_company_mono_white_logo' },
    alt: { label: 'Alt', type: 'text', defaultValue: 'Management Company' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://multihub.io' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt', type: 'text', defaultValue: 'Agency' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://repli.com' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Nav column link definitions
const col2Link1Def = navLinkDef('About Col — Link 1', 'About Us', 'https://www.alapts.com/about-us');
const col2Link2Def = navLinkDef('About Col — Link 2', 'Our Team', '/team');

const col3Link1Def = navLinkDef('Communities Col — Link 1', 'Florida', '/properties/florida');
const col3Link2Def = navLinkDef('Communities Col — Link 2', 'Georgia', '/properties/georgia');
const col3Link3Def = navLinkDef('Communities Col — Link 3', 'Texas', '/properties/texas');
const col3Link4Def = navLinkDef('Communities Col — Link 4', 'Tennessee', '/properties/tennessee');
const col3Link5Def = navLinkDef('Communities Col — Link 5', 'North Carolina', '/properties/north-carolina');
const col3Link6Def = navLinkDef('Communities Col — Link 6', 'South Carolina', '/properties/south-carolina');
const col3Link7Def = navLinkDef('Communities Col — Link 7', 'Alabama', '/properties/alabama');
const col3Link8Def = navLinkDef('Communities Col — Link 8', 'Virginia', '/properties/virginia');

const col4Link1Def = navLinkDef('Standalone Link 1', 'Careers', '/careers');
const col4Link2Def: SlotGroupDefinition = {
  label: 'Standalone Link 2',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/landmark-360' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Landmark 360' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};
const col4Link3Def = navLinkDef('Standalone Link 3', 'Investors', '/investors');
const col4Link4Def = navLinkDef('Standalone Link 4', 'News', '/news');
const col4Link5Def = navLinkDef('Standalone Link 5', 'Contact Us', 'https://www.alapts.com/contact-us');

// ─── Component ────────────────────────────────────────────────────────────────

export function Footer() {
  // Text tokens
  const disclaimer = useContentToken('footer.disclaimer');
  const copyrightName = useContentToken('footer.copyright_name');
  const copyrightSuffix = useContentToken('footer.copyright_suffix');
  const accessibilityLabel = useContentToken('footer.accessibility_link_label');
  const privacyLabel = useContentToken('footer.privacy_policy_link_label');
  const sitemapLabel = useContentToken('footer.sitemap_link_label');
  const managedByText = useContentToken('footer.managed_by_text');
  const poweredByText = useContentToken('footer.powered_by_text');
  const designByText = useContentToken('footer.design_by_text');
  const companyOfficeLabel = useContentToken('footer.company_office_label');
  const companyAddress = useContentToken('footer.company_address');
  const col2Heading = useContentToken('footer.nav_col2_heading');
  const col3Heading = useContentToken('footer.nav_col3_heading');

  // Property fields
  const propertyName = usePropertyField('property_name') as string | null;
  const addressUrlFallback = usePropertyAddressUrl();

  const currentYear = new Date().getFullYear();

  // Pick the default company logo based on the secondary surface contrast:
  // a light secondary background (with dark text-on-secondary) needs a dark
  // logo; otherwise the white/light logo reads on the dark band.
  const secondaryColor = useSecondaryColor();
  const textOnSecondary = useTokens().color.textOnSecondary;
  const defaultCompanyLogoUrl =
    isLightColor(secondaryColor) && isDarkColor(textOnSecondary)
      ? COMPANY_LOGO_FOR_LIGHT_SECONDARY
      : COMPANY_LOGO_FOR_DARK_SECONDARY;

  // Slot groups
  const propertyAddress = useSlotGroup('footer.property_address', propertyAddressDef);
  const addressText = propertyAddress.label || '';
  const addressUrl = propertyAddress.url || addressUrlFallback || '';
  const propertyPhone = useSlotGroup('footer.property_phone', propertyPhoneDef);
  const phone = propertyPhone.phone || '';
  const companyLogo = useSlotGroup('footer.company_logo', companyLogoDef);
  const companyLogoImage = companyLogo.image || defaultCompanyLogoUrl;
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const companyInstagram = useSlotGroup('footer.company_instagram', companyInstagramDef);
  const companyLinkedin = useSlotGroup('footer.company_linkedin', companyLinkedinDef);
  const companyFacebook = useSlotGroup('footer.company_facebook', companyFacebookDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);

  // Nav link slot groups
  const c2l1 = useSlotGroup('footer.nav_col2_link1', col2Link1Def);
  const c2l2 = useSlotGroup('footer.nav_col2_link2', col2Link2Def);
  const c3l1 = useSlotGroup('footer.nav_col3_link1', col3Link1Def);
  const c3l2 = useSlotGroup('footer.nav_col3_link2', col3Link2Def);
  const c3l3 = useSlotGroup('footer.nav_col3_link3', col3Link3Def);
  const c3l4 = useSlotGroup('footer.nav_col3_link4', col3Link4Def);
  const c3l5 = useSlotGroup('footer.nav_col3_link5', col3Link5Def);
  const c3l6 = useSlotGroup('footer.nav_col3_link6', col3Link6Def);
  const c3l7 = useSlotGroup('footer.nav_col3_link7', col3Link7Def);
  const c3l8 = useSlotGroup('footer.nav_col3_link8', col3Link8Def);
  const c4l1 = useSlotGroup('footer.nav_col4_link1', col4Link1Def);
  const c4l2 = useSlotGroup('footer.nav_col4_link2', col4Link2Def);
  const c4l3 = useSlotGroup('footer.nav_col4_link3', col4Link3Def);
  const c4l4 = useSlotGroup('footer.nav_col4_link4', col4Link4Def);
  const c4l5 = useSlotGroup('footer.nav_col4_link5', col4Link5Def);

  // Office hours collection
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  const hasSocial = !!(facebookLink.url || instagramLink.url);

  const col2Links = [c2l1, c2l2];
  const col3Links = [c3l1, c3l2, c3l3, c3l4, c3l5, c3l6, c3l7, c3l8];
  const col4Links = [c4l1, c4l2, c4l3, c4l4, c4l5];

  return (
    <footer className="footer-al footer-al-bg text-[var(--vibe-color-text-on-secondary)]">

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Band 1 — Disclaimer                                    */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-center">
        <EditableToken
          path="footer.disclaimer"
          defaultValue="Prices and special offers valid for new residents only. Pricing and availability subject to change at any time."
          as="p"
          className="font-body text-xs text-[var(--vibe-color-text-on-secondary)]/70 leading-relaxed"
        >
          {disclaimer}
        </EditableToken>
      </div>

      <div className="border-t border-[var(--vibe-color-text-on-secondary)]/15" />

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Band 2 — Property info (name · address · phone / hours / socials) */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        {/* Row 1: property name · address · phone */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 mb-3">
          {hasValue(propertyName) && (
            <span className="font-body text-sm font-semibold text-[var(--vibe-color-text-on-secondary)]">
              {propertyName}
            </span>
          )}
          {hasValue(addressText) && (
            <EditableSlotGroup groupPath="footer.property_address" definition={propertyAddressDef}>
              <a
                href={addressUrl || '#'}
                target={propertyAddress._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-sm text-[var(--vibe-color-text-on-secondary)]/80 hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
              >
                {addressText}
              </a>
            </EditableSlotGroup>
          )}
          {hasValue(phone) && (
            <EditableSlotGroup groupPath="footer.property_phone" definition={propertyPhoneDef}>
              <a
                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-sm text-[var(--vibe-color-text-on-secondary)]/80 hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
              >
                {formatPhone(phone)}
              </a>
            </EditableSlotGroup>
          )}
        </div>

        {/* Row 2: office hours inline */}
        {groupedHours.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 mb-5">
            {groupedHours.map((g, i) => (
              <span key={i} className="font-body text-sm text-[var(--vibe-color-text-on-secondary)]/70">
                {g.label}: {g.hours}
              </span>
            ))}
          </div>
        )}

        {/* Row 3: property social icons */}
        {hasSocial && (
          <div className="flex items-center justify-center gap-3">
            {facebookLink.url && (
              <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                <a
                  href={facebookLink.url}
                  target={facebookLink._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="footer-al-social-icon"
                >
                  <FacebookSvg />
                </a>
              </EditableSlotGroup>
            )}
            {instagramLink.url && (
              <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                <a
                  href={instagramLink.url}
                  target={instagramLink._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="footer-al-social-icon"
                >
                  <InstagramSvg />
                </a>
              </EditableSlotGroup>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-[var(--vibe-color-text-on-secondary)]/15" />

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Band 3 — 4-column nav grid                             */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr] gap-10 lg:gap-8">

          {/* Col 1: Company logo + corporate office + company socials */}
          <div>
            <EditableSlotGroup groupPath="footer.company_logo" definition={companyLogoDef}>
              <a
                href={companyLogo.url || '/'}
                target={companyLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="inline-block mb-6"
              >
                {companyLogoImage ? (
                  <VibesitesImage
                    url={companyLogoImage}
                    alt={companyLogo.alt || 'American Landmark Apartments'}
                    className="h-14 w-auto max-w-[220px] object-contain"
                  />
                ) : (
                  <span className="font-heading text-xl font-light text-[var(--vibe-color-text-on-secondary)] tracking-wide">
                    American Landmark
                  </span>
                )}
              </a>
            </EditableSlotGroup>

            <EditableToken
              path="footer.company_office_label"
              defaultValue="Corporate Office"
              as="p"
              className="font-body text-sm font-bold text-[var(--vibe-color-text-on-secondary)] mb-2"
            >
              {companyOfficeLabel}
            </EditableToken>

            <EditableToken
              path="footer.company_address"
              defaultValue="4890 W Kennedy Blvd, Ste 900&#10;Tampa, FL 33609"
              as="p"
              className="font-body text-sm text-[var(--vibe-color-text-on-secondary)]/70 leading-relaxed mb-6 whitespace-pre-line"
            >
              {companyAddress}
            </EditableToken>

            {/* Company social icons */}
            <div className="flex items-center gap-3">
              {companyInstagram.url && (
                <EditableSlotGroup groupPath="footer.company_instagram" definition={companyInstagramDef}>
                  <a href={companyInstagram.url} target={companyInstagram._target || '_blank'} rel="noopener noreferrer" aria-label="Company Instagram" className="footer-al-social-icon">
                    <InstagramSvg />
                  </a>
                </EditableSlotGroup>
              )}
              {companyLinkedin.url && (
                <EditableSlotGroup groupPath="footer.company_linkedin" definition={companyLinkedinDef}>
                  <a href={companyLinkedin.url} target={companyLinkedin._target || '_blank'} rel="noopener noreferrer" aria-label="Company LinkedIn" className="footer-al-social-icon">
                    <LinkedInSvg />
                  </a>
                </EditableSlotGroup>
              )}
              {companyFacebook.url && (
                <EditableSlotGroup groupPath="footer.company_facebook" definition={companyFacebookDef}>
                  <a href={companyFacebook.url} target={companyFacebook._target || '_blank'} rel="noopener noreferrer" aria-label="Company Facebook" className="footer-al-social-icon">
                    <FacebookSvg />
                  </a>
                </EditableSlotGroup>
              )}
            </div>
          </div>

          {/* Col 2: About nav column */}
          <div>
            <EditableToken
              path="footer.nav_col2_heading"
              defaultValue="About Us"
              as="p"
              className="font-body text-sm font-bold text-[var(--vibe-color-text-on-secondary)] mb-4"
            >
              {col2Heading}
            </EditableToken>
            <ul className="space-y-3">
              {col2Links.map((lnk, i) => (
                lnk.url ? (
                  <li key={i}>
                    <EditableSlotGroup groupPath={`footer.nav_col2_link${i + 1}`} definition={[col2Link1Def, col2Link2Def][i]}>
                      <a href={lnk.url} target={lnk._target || '_self'} className="font-body text-sm text-[var(--vibe-color-text-on-secondary)]/70 hover:text-[var(--vibe-color-text-on-secondary)] transition-colors">
                        {lnk.label}
                      </a>
                    </EditableSlotGroup>
                  </li>
                ) : null
              ))}
            </ul>
          </div>

          {/* Col 3: Communities nav column — hidden for now (states list).
              Wrappers/schema kept intact so it can be re-enabled later. */}
          <div className="hidden">
            <EditableToken
              path="footer.nav_col3_heading"
              defaultValue="Communities"
              as="p"
              className="font-body text-sm font-bold text-[var(--vibe-color-text-on-secondary)] mb-4"
            >
              {col3Heading}
            </EditableToken>
            <ul className="space-y-3">
              {col3Links.map((lnk, i) => (
                lnk.url ? (
                  <li key={i}>
                    <EditableSlotGroup groupPath={`footer.nav_col3_link${i + 1}`} definition={[col3Link1Def, col3Link2Def, col3Link3Def, col3Link4Def, col3Link5Def, col3Link6Def, col3Link7Def, col3Link8Def][i]}>
                      <a href={lnk.url} target={lnk._target || '_self'} className="font-body text-sm text-[var(--vibe-color-text-on-secondary)]/70 hover:text-[var(--vibe-color-text-on-secondary)] transition-colors">
                        {lnk.label}
                      </a>
                    </EditableSlotGroup>
                  </li>
                ) : null
              ))}
            </ul>
          </div>

          {/* Col 4: Standalone heading-links */}
          <div className="space-y-4">
            {col4Links.map((lnk, i) => (
              lnk.url ? (
                <div key={i}>
                  <EditableSlotGroup groupPath={`footer.nav_col4_link${i + 1}`} definition={[col4Link1Def, col4Link2Def, col4Link3Def, col4Link4Def, col4Link5Def][i]}>
                    <a href={lnk.url} target={lnk._target || '_self'} className="font-body text-sm font-bold text-[var(--vibe-color-text-on-secondary)] hover:text-[var(--vibe-color-text-on-secondary)]/80 transition-colors">
                      {lnk.label}
                    </a>
                  </EditableSlotGroup>
                </div>
              ) : null
            ))}
          </div>

        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* Bottom bar — compliance + attribution                  */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="border-t border-[var(--vibe-color-text-on-secondary)]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Compliance icons + legal links */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-5 text-[var(--vibe-color-text-on-secondary)]/60">
              <FairHousingIcon size={26} color="currentColor" />
              <PetsAllowedIcon size={26} color="currentColor" />
              <WheelchairIcon size={26} color="currentColor" />
            </div>
            <nav className="flex items-center gap-2 font-body text-xs text-[var(--vibe-color-text-on-secondary)]/60" aria-label="Legal links">
              <a href="/accessibility" className="hover:text-[var(--vibe-color-text-on-secondary)]/90 transition-colors">
                <EditableToken path="footer.accessibility_link_label" defaultValue="Accessibility" as="span">
                  {accessibilityLabel}
                </EditableToken>
              </a>
              <span aria-hidden="true">·</span>
              <a href="/privacy-policy" className="hover:text-[var(--vibe-color-text-on-secondary)]/90 transition-colors">
                <EditableToken path="footer.privacy_policy_link_label" defaultValue="Privacy Policy" as="span">
                  {privacyLabel}
                </EditableToken>
              </a>
              <span aria-hidden="true">·</span>
              <a href="/sitemap" className="hover:text-[var(--vibe-color-text-on-secondary)]/90 transition-colors">
                <EditableToken path="footer.sitemap_link_label" defaultValue="Sitemap" as="span">
                  {sitemapLabel}
                </EditableToken>
              </a>
            </nav>
          </div>

          {/* Copyright + attribution */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4 border-t border-[var(--vibe-color-text-on-secondary)]/10">
            <p className="font-body text-[10px] text-[var(--vibe-color-text-on-secondary)]/50">
              &copy; {currentYear}{' '}
              <EditableToken path="footer.copyright_name" defaultField="property_name" defaultValue="Property Name" as="span">
                {copyrightName}
              </EditableToken>
              {'. '}
              <EditableToken path="footer.copyright_suffix" defaultValue="All Rights Reserved." as="span">
                {copyrightSuffix}
              </EditableToken>
            </p>

            <div className="flex items-center gap-5">
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.managed_by_text" defaultValue="Managed by" as="span" className="font-body text-[10px] text-[var(--vibe-color-text-on-secondary)]/50">
                    {managedByText}
                  </EditableToken>
                  {managedByLogo.image && (
                    <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer">
                      <VibesitesImage url={managedByLogo.image} alt={managedByLogo.alt || 'Management Company'} className="h-3.5 w-auto max-w-[90px] object-contain opacity-60 hover:opacity-90 transition-opacity" />
                    </a>
                  )}
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.powered_by_text" defaultValue="Powered by" as="span" className="font-body text-[10px] text-[var(--vibe-color-text-on-secondary)]/50">
                    {poweredByText}
                  </EditableToken>
                  <a href={poweredByLogo.url || 'https://multihub.io'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-60 hover:opacity-90 transition-opacity">
                    <VibesitesImage url={poweredByLogo.image || multihubLogoMonoWhiteUrl} alt={poweredByLogo.alt || 'Multihub'} className="h-3.5 w-auto max-w-[80px] object-contain" />
                  </a>
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.design_by_text" defaultValue="Website Design by" as="span" className="font-body text-[10px] text-[var(--vibe-color-text-on-secondary)]/50">
                    {designByText}
                  </EditableToken>
                  <a href={designByLogo.url || 'https://repli.com'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-60 hover:opacity-90 transition-opacity">
                    <VibesitesImage url={designByLogo.image || repliWhiteLogoUrl} alt={designByLogo.alt || 'Agency'} className="h-3.5 w-auto max-w-[80px] object-contain" />
                  </a>
                </div>
              </EditableSlotGroup>
            </div>
          </div>

        </div>
      </div>

      {/* ─── Hidden schema-lint anchors ─────────────────────────────────────── */}
      {/* The nav-column links render through .map() with a template-literal      */}
      {/* groupPath, so the source-level linter can't see the literal paths.      */}
      {/* These hidden anchors carry every canonical literal path. They never     */}
      {/* render visibly and are never interactive.                               */}
      <div className="hidden" style={{ display: 'none' }}>
        <EditableSlotGroup groupPath="footer.nav_col2_link1" definition={col2Link1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col2_link2" definition={col2Link2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link1" definition={col3Link1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link2" definition={col3Link2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link3" definition={col3Link3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link4" definition={col3Link4Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link5" definition={col3Link5Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link6" definition={col3Link6Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link7" definition={col3Link7Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col3_link8" definition={col3Link8Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col4_link1" definition={col4Link1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col4_link2" definition={col4Link2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col4_link3" definition={col4Link3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col4_link4" definition={col4Link4Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="footer.nav_col4_link5" definition={col4Link5Def}><span /></EditableSlotGroup>
      </div>

      <style>{`
        .footer-al-bg {
          background: linear-gradient(
            160deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
        }
        .footer-al-social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--vibe-color-text-on-secondary) 25%, transparent);
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 55%, transparent);
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .footer-al-social-icon:hover {
          border-color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 70%, transparent);
          color: var(--vibe-color-text-on-secondary);
          background: color-mix(in srgb, var(--vibe-color-text-on-secondary) 8%, transparent);
        }
      `}</style>
    </footer>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.disclaimer': {
      label: 'Disclaimer Text',
      type: 'text',
      defaultValue: 'Prices and special offers valid for new residents only. Pricing and availability subject to change at any time.',
    },
    'footer.company_office_label': {
      label: 'Corporate Office Label',
      type: 'text',
      defaultValue: 'Corporate Office',
    },
    'footer.company_address': {
      label: 'Corporate Office Address',
      type: 'text',
      defaultValue: '4890 W Kennedy Blvd, Ste 900\nTampa, FL 33609',
    },
    'footer.nav_col2_heading': {
      label: 'About Column Heading',
      type: 'text',
      defaultValue: 'About Us',
    },
    'footer.nav_col3_heading': {
      label: 'Communities Column Heading',
      type: 'text',
      defaultValue: 'Communities',
    },
    'footer.copyright_name': {
      label: 'Copyright Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Name',
    },
    'footer.copyright_suffix': {
      label: 'Copyright Suffix',
      type: 'text',
      defaultValue: 'All Rights Reserved.',
    },
    'footer.accessibility_link_label': { label: 'Accessibility Link Label', type: 'text', defaultValue: 'Accessibility' },
    'footer.privacy_policy_link_label': { label: 'Privacy Policy Link Label', type: 'text', defaultValue: 'Privacy Policy' },
    'footer.sitemap_link_label': { label: 'Sitemap Link Label', type: 'text', defaultValue: 'Sitemap' },
    'footer.managed_by_text': { label: 'Managed By Text', type: 'text', defaultValue: 'Managed by' },
    'footer.powered_by_text': { label: 'Powered By Text', type: 'text', defaultValue: 'Powered by' },
    'footer.design_by_text': { label: 'Website Design By Text', type: 'text', defaultValue: 'Website Design by' },
  },
  slotGroups: {
    'footer.property_address': propertyAddressDef,
    'footer.property_phone': propertyPhoneDef,
    'footer.company_logo': companyLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.company_instagram': companyInstagramDef,
    'footer.company_linkedin': companyLinkedinDef,
    'footer.company_facebook': companyFacebookDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.nav_col2_link1': col2Link1Def,
    'footer.nav_col2_link2': col2Link2Def,
    'footer.nav_col3_link1': col3Link1Def,
    'footer.nav_col3_link2': col3Link2Def,
    'footer.nav_col3_link3': col3Link3Def,
    'footer.nav_col3_link4': col3Link4Def,
    'footer.nav_col3_link5': col3Link5Def,
    'footer.nav_col3_link6': col3Link6Def,
    'footer.nav_col3_link7': col3Link7Def,
    'footer.nav_col3_link8': col3Link8Def,
    'footer.nav_col4_link1': col4Link1Def,
    'footer.nav_col4_link2': col4Link2Def,
    'footer.nav_col4_link3': col4Link3Def,
    'footer.nav_col4_link4': col4Link4Def,
    'footer.nav_col4_link5': col4Link5Def,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
